import React, { useState } from 'react';
import { Camera, Video, Film, Check, X } from 'lucide-react';
import { useLiveEditorScope } from '../../context/LiveEditorScopeContext';
import InsertImageModal from '../blog/InsertImageModal';
import VideoUploadDropzone from '../admin/VideoUploadDropzone';

interface EditableMediaProps {
  currentImageUrl?: string;
  currentVideoUrl?: string;
  onImageChange?: (newUrl: string) => void;
  onVideoChange?: (newVideoUrl: string) => void;
  children: React.ReactNode;
  label?: string;
  className?: string;
  canEdit?: boolean;
}

export default function EditableMedia({
  currentImageUrl,
  currentVideoUrl,
  onImageChange,
  onVideoChange,
  children,
  label = 'Media Banner',
  className = '',
  canEdit: canEditProp
}: EditableMediaProps) {
  const scope = useLiveEditorScope();
  const canEdit = typeof canEditProp === 'boolean' ? canEditProp : scope.canEdit;
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [tempVideoUrl, setTempVideoUrl] = useState(currentVideoUrl || '');

  if (!canEdit) {
    return <>{children}</>;
  }

  const isAbsolute = className.includes('absolute');
  const posClass = isAbsolute ? '' : 'relative';
  const sizeClass = isAbsolute && !className.includes('w-') ? 'w-full h-full' : '';

  return (
    <div className={`${posClass} ${sizeClass} group/media ${className}`}>
      {children}

      {/* Admin Floating Controls Badge */}
      <div className="absolute top-4 right-4 z-40 flex items-center gap-2 opacity-90 group-hover/media:opacity-100 transition-opacity">
        {onImageChange && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowImageModal(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1C1817]/85 hover:bg-black text-white text-xs font-semibold rounded-lg shadow-xl backdrop-blur-md border border-white/20 transition-all cursor-pointer transform hover:scale-105"
            title={`Ganti gambar untuk ${label}`}
          >
            <Camera className="w-3.5 h-3.5 text-[#E6C687]" />
            <span>Ganti Gambar</span>
          </button>
        )}

        {onVideoChange && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTempVideoUrl(currentVideoUrl || '');
              setShowVideoModal(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1C1817]/85 hover:bg-black text-white text-xs font-semibold rounded-lg shadow-xl backdrop-blur-md border border-white/20 transition-all cursor-pointer transform hover:scale-105"
            title={`Ganti video untuk ${label}`}
          >
            <Film className="w-3.5 h-3.5 text-[#E6C687]" />
            <span>{currentVideoUrl ? 'Ganti Video' : '+ Pasang Video'}</span>
          </button>
        )}
      </div>

      {/* Image Crop & Upload Modal */}
      {showImageModal && onImageChange && (
        <InsertImageModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          onInsert={(url) => {
            onImageChange(url);
            setShowImageModal(false);
          }}
        />
      )}

      {/* Video URL Prompt & Upload Modal */}
      {showVideoModal && onVideoChange && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          data-lenis-prevent="true"
        >
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-border-subtle">
            <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-brand-wine" />
                <h3 className="text-base font-serif font-bold text-ink">Atur Video Background ({label})</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="text-muted hover:text-ink p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-bold text-ink block mb-1.5">
                  Unggah Berkas Video Langsung:
                </label>
                <VideoUploadDropzone
                  label="Pilih Video MP4/WebM"
                  onUploaded={(uploadedUrl) => {
                    setTempVideoUrl(uploadedUrl);
                  }}
                />
              </div>

              <div className="relative flex py-1 items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-3 text-[11px] text-gray-400 font-medium">atau gunakan tautan</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <div>
                <label className="text-xs font-semibold text-muted block mb-1">
                  URL Video (MP4 / WebM / Google Drive):
                </label>
                <input
                  type="text"
                  value={tempVideoUrl}
                  onChange={(e) => setTempVideoUrl(e.target.value)}
                  placeholder="https://... atau /uploads/..."
                  className="w-full px-3 py-2 text-xs border border-border-subtle rounded-lg focus:outline-none focus:border-brand-wine focus:ring-1 focus:ring-brand-wine font-mono text-ink bg-gray-50/50"
                />
              </div>

              <div className="p-3 bg-amber-50/70 border border-amber-200/50 rounded-xl flex items-start justify-between gap-3">
                <div className="text-[11px] text-amber-900 leading-relaxed">
                  💡 <strong>Ingin pakai foto statis saja?</strong> Klik tombol di samping untuk menghapus video agar background foto tampil penuh tanpa tertimpa video.
                </div>
                {tempVideoUrl ? (
                  <button
                    type="button"
                    onClick={() => setTempVideoUrl('')}
                    className="flex-shrink-0 px-2.5 py-1 text-xs font-bold text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors cursor-pointer"
                  >
                    Hapus Video
                  </button>
                ) : (
                  <span className="flex-shrink-0 text-[10px] text-emerald-700 font-bold bg-emerald-100 px-2 py-0.5 rounded">
                    Foto Aktif
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-2 text-xs font-semibold text-muted hover:text-ink hover:bg-gray-100 rounded-lg transition-colors"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => {
                  onVideoChange(tempVideoUrl.trim());
                  setShowVideoModal(false);
                }}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-wine hover:bg-brand-wine/90 text-white text-xs font-bold rounded-lg shadow transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
                <span>Simpan Perubahan</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
